import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { switchMap, mergeMap, map, catchError, tap } from 'rxjs/operators';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import {
  loadAllDrawingMessages,
  loadAllDrawingMessagesSuccess,
  loadAllDrawingMessagesFailure,
  loadDrawingMessages,
  loadDrawingMessagesSuccess,
  loadDrawingMessagesFailure,
  createDrawingMessage,
  createDrawingMessageSuccess,
  createDrawingMessageFailure,
  deleteDrawingMessage,
  deleteDrawingMessageSuccess,
  deleteDrawingMessageFailure,
  updateDrawingMessage,
  updateDrawingMessageSuccess,
  updateDrawingMessageFailure,
} from './drawing-message.action';
import { DrawingService } from '../../../drawing/data-access/drawing.service';
import { DrawingMessageAttachmentService } from '../../../drawing/data-access/drawing-message-attachment.service';

@Injectable()
export class DrawingMessageEffects {

  loadAllDrawingMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAllDrawingMessages),
      switchMap(({ drawingIds }) =>
        this.drawingService.getDrawingMessagesByDrawingIds(drawingIds).pipe(
          map(messagesByDrawingId => loadAllDrawingMessagesSuccess({ messagesByDrawingId })),
          catchError(() => of(loadAllDrawingMessagesFailure({ error: 'Failed to load messages' })))
        )
      )
    )
  );

  loadDrawingMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDrawingMessages),
      mergeMap(({ drawingId }) =>
        this.drawingService.getDrawingMessages(drawingId).pipe(
          map(messages => loadDrawingMessagesSuccess({ drawingId, messages })),
          catchError(() => of(loadDrawingMessagesFailure({ error: 'Failed to load messages' })))
        )
      )
    )
  );

  createDrawingMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createDrawingMessage),
      mergeMap(({ drawingId, firstName, lastName, message, files }) =>
        this.drawingService.createDrawingMessage({ drawingId, firstName, lastName, message }).pipe(
          switchMap(response => {
            const newMessage = { ...response.data, firstName, lastName };
            if (!files || files.length === 0) {
              return of(createDrawingMessageSuccess({ drawingId, message: newMessage }));
            }
            return this.attachmentService.uploadAttachments(newMessage.id, files).pipe(
              map(attachments => createDrawingMessageSuccess({
                drawingId,
                message: { ...newMessage, attachments },
              })),
              catchError(() => of(
                createDrawingMessageSuccess({ drawingId, message: newMessage }),
                createDrawingMessageFailure({ error: 'Message sent, but attachments failed to upload' }),
              ))
            );
          }),
          catchError(() => of(createDrawingMessageFailure({ error: 'Failed to send message' })))
        )
      )
    )
  );

  deleteDrawingMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDrawingMessage),
      switchMap(({ id, drawingId }) =>
        this.drawingService.deleteDrawingMessage(id).pipe(
          map(() => deleteDrawingMessageSuccess({ id, drawingId })),
          catchError(() => of(deleteDrawingMessageFailure({ error: 'Failed to delete message' })))
        )
      )
    )
  );

  updateDrawingMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDrawingMessage),
      switchMap(({ id, drawingId, message, removedAttachmentIds, newFiles }) => {
        const removeIds = removedAttachmentIds ?? [];
        const files = newFiles ?? [];
        return this.drawingService.updateDrawingMessage(id, message).pipe(
          switchMap(() => {
            const deletes$ = removeIds.length
              ? forkJoin(removeIds.map(aid => this.attachmentService.deleteAttachment(aid)))
              : of([] as any[]);
            return deletes$.pipe(
              switchMap(() => files.length
                ? this.attachmentService.uploadAttachments(id, files)
                : of([])
              ),
              map(addedAttachments => updateDrawingMessageSuccess({
                id, drawingId, message,
                removedAttachmentIds: removeIds,
                addedAttachments,
              })),
            );
          }),
          catchError(() => of(updateDrawingMessageFailure({ error: 'Failed to update message' })))
        );
      })
    )
  );

  toastErrorMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createDrawingMessageFailure, deleteDrawingMessageFailure, loadDrawingMessagesFailure, loadAllDrawingMessagesFailure, updateDrawingMessageFailure),
      tap(({ error }) => {
        this.messageService.add({ severity: 'error', summary: error } as ToastMessageOptions);
      })
    ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private drawingService: DrawingService,
    private attachmentService: DrawingMessageAttachmentService,
    private messageService: MessageService,
  ) {}
}
